import React, { useEffect, useMemo, useRef, useState } from "react"
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import useInstallPrompt from "./hooks/useInstallPrompt"

function parseGateIdFromPathname(pathname) {
  const parts = pathname.split("/").filter(Boolean)
  const maybeId = parts[parts.length - 1]
  return isSupportedId(maybeId) ? maybeId : null
}

function sessionIdFromQuery(search) {
  const params = new URLSearchParams(search)
  const value = params.get("session_id")
  return isSupportedId(value) ? value : null
}

function isSupportedId(value) {
  if (!value) return false

  // Keep numeric support for legacy URLs and accept UUIDs used by current models.
  return /^\d+$/.test(value) || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

function formatMoney(value) {
  const amount = Number(value || 0)
  return `$${amount.toFixed(2)}`
}

function formatClock(value) {
  if (!value) return "-"
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

function formatFeeNotice(fixedAmount, percentage) {
  const parts = []
  const fixed = Number(fixedAmount || 0)
  const pct = Number(percentage || 0)

  if (fixed > 0) parts.push(`${formatMoney(fixed)} processing fee`)
  if (pct > 0) parts.push(`${pct.toFixed(2)}% merchant fee`)
  if (parts.length === 0) return null

  return `plus ${parts.join(" + ")}.`
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+$/.test(value)
}

function normalizePlateForLookup(value) {
  return value.toString().toUpperCase().replace(/\s+/g, "")
}

function extractOccupancy(payload) {
  if (!payload) return null

  if (payload.occupancy) return payload.occupancy

  if (
    Object.prototype.hasOwnProperty.call(payload, "total_bays") &&
    Object.prototype.hasOwnProperty.call(payload, "current_occupancy") &&
    Object.prototype.hasOwnProperty.call(payload, "available_spots")
  ) {
    return {
      total_bays: Number(payload.total_bays || 0),
      current_occupancy: Number(payload.current_occupancy || 0),
      available_spots: Number(payload.available_spots || 0),
      percentage_full: Number(payload.percentage_full || 0)
    }
  }

  return null
}

function stepForResumedSession(status, requiresPayment) {
  if (status === "active") return "active"
  if (status === "payment_authorised") return "authorised"
  if (status === "requires_payment") return requiresPayment ? "payment" : "authorised"
  return "vehicle"
}

async function apiRequest(url, options = {}) {
  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    ...options
  })

  const bodyText = await response.text()
  let json = {}
  if (bodyText) {
    try {
      json = JSON.parse(bodyText)
    } catch (_e) {
      json = {}
    }
  }

  if (!response.ok) {
    throw new Error(json.error || "Request failed")
  }

  return json
}

function StripeCardAuthoriseForm({
  busy,
  error,
  onAuthorise,
  buttonText = "Authorise Payment",
  secureCopy = "Secured Payment. You will only be charged when you exit."
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [cardError, setCardError] = useState("")

  async function handleSubmit() {
    setCardError("")

    if (!stripe || !elements) {
      setCardError("Payment form is still loading")
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setCardError("Card form is unavailable")
      return
    }

    const result = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement
    })

    if (result.error) {
      setCardError(result.error.message || "Unable to authorise card")
      return
    }

    if (!result.paymentMethod?.id) {
      setCardError("Unable to authorise card")
      return
    }

    onAuthorise(result.paymentMethod.id)
  }

  return (
    <>
      {error ? <div className="parking-inline-error parking-inline-error-in-card">{error}</div> : null}
      {cardError ? <div className="parking-inline-error parking-inline-error-in-card">{cardError}</div> : null}
      <label htmlFor="stripe-card-element">Card Details</label>
      <div id="stripe-card-element" className="parking-stripe-card">
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                color: "#2c3f59",
                fontSize: "20px",
                fontFamily: "\"Avenir Next\", \"Avenir\", \"Segoe UI\", sans-serif",
                "::placeholder": {
                  color: "#8392a8"
                }
              },
              invalid: {
                color: "#b42338"
              }
            }
          }}
        />
      </div>

      <div className="parking-secure-copy">{secureCopy}</div>

      <button
        className="parking-button parking-button-primary"
        type="button"
        onClick={handleSubmit}
        disabled={busy || !stripe}
      >
        {buttonText}
      </button>
    </>
  )
}

export default function ParkingPage() {
  const gateId = useMemo(() => parseGateIdFromPathname(window.location.pathname), [])
  const sessionIdFromUrl = useMemo(() => sessionIdFromQuery(window.location.search), [])
  const { isInstallable, isInstalled, promptInstall } = useInstallPrompt()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  const [pageData, setPageData] = useState(null)
  const [step, setStep] = useState("landing")

  const [plate, setPlate] = useState("")
  const [email, setEmail] = useState("")
  const [rememberEmail, setRememberEmail] = useState(true)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const emailManuallyEditedRef = useRef(false)
  const autoFilledEmailRef = useRef("")
  const rememberedLookupSeqRef = useRef(0)

  const [parkingSession, setParkingSession] = useState(null)
  const [metrics, setMetrics] = useState(null)
  const [summary, setSummary] = useState(null)
  const [exitGates, setExitGates] = useState([])
  const [openedExitGate, setOpenedExitGate] = useState(null)
  const [exitConfirmArmed, setExitConfirmArmed] = useState(false)
  const [occupancy, setOccupancy] = useState(null)
  const [installDismissed, setInstallDismissed] = useState(false)
  const [gateWarmupKeys, setGateWarmupKeys] = useState({})

  useEffect(() => {
    if (!gateId) {
      setError("Invalid parking URL")
      setLoading(false)
      return
    }

    async function load() {
      try {
        const data = await apiRequest(`/api/v1/parking/${gateId}`)
        setPageData(data)
        setExitGates(data.exit_gates || [])
        setOccupancy(extractOccupancy(data))

        if (!sessionIdFromUrl && data.gate?.direction === "exit") {
          setStep("exit_plate")
        }

        if (!sessionIdFromUrl && data.site?.ai_vehicle_identification_enabled) {
          try {
            const latestLpr = await apiRequest(`/api/v1/parking/${gateId}/latest_lpr`)
            if (latestLpr?.plate) setPlate(latestLpr.plate)
          } catch (_latestLprError) {
            // LPR prefill is only a convenience; keep the normal manual entry path.
          }
        }

        if (sessionIdFromUrl) {
          const statusData = await apiRequest(`/api/v1/parking/${gateId}/status?session_id=${sessionIdFromUrl}`)
          const resumedSession = statusData.parking_session
          const requiresPayment = Boolean(data?.stripe?.configured)

          if (resumedSession) {
            setParkingSession(resumedSession)
            setMetrics(statusData.metrics || null)
            setStep(stepForResumedSession(resumedSession.status, requiresPayment))
            setPlate(resumedSession.plate || "")
            setEmail(resumedSession.email || "")
            const resumedOccupancy = extractOccupancy(statusData)
            if (resumedOccupancy) setOccupancy(resumedOccupancy)
          }
        }
      } catch (requestError) {
        setError(requestError.message)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [gateId, sessionIdFromUrl])

  useEffect(() => {
    if (!parkingSession?.id || step !== "active") return undefined

    let stopped = false

    async function loadStatus() {
      try {
        const data = await apiRequest(`/api/v1/parking/${gateId}/status?session_id=${parkingSession.id}`)
        if (stopped) return
        setMetrics(data.metrics)
        setParkingSession(data.parking_session)
      } catch (_requestError) {
        if (!stopped) setError("Could not refresh parking status")
      }
    }

    loadStatus()
    const interval = window.setInterval(loadStatus, 1000)

    return () => {
      stopped = true
      window.clearInterval(interval)
    }
  }, [gateId, parkingSession?.id, step])

  useEffect(() => {
    if (!gateId || !pageData || sessionIdFromUrl || pageData.gate?.direction === "exit") return undefined

    const normalizedPlate = normalizePlateForLookup(plate)
    const lookupSeq = rememberedLookupSeqRef.current + 1
    rememberedLookupSeqRef.current = lookupSeq
    if (!normalizedPlate) {
      setEmail((currentEmail) => {
        if (autoFilledEmailRef.current && currentEmail === autoFilledEmailRef.current) {
          autoFilledEmailRef.current = ""
          return ""
        }

        return currentEmail
      })
      return undefined
    }

    const timer = window.setTimeout(async () => {
      if (emailManuallyEditedRef.current) return

      try {
        const data = await apiRequest(`/api/v1/parking/${gateId}/remembered_email?plate=${encodeURIComponent(normalizedPlate)}`)
        if (rememberedLookupSeqRef.current !== lookupSeq || emailManuallyEditedRef.current) return

        setEmail((currentEmail) => {
          if (emailManuallyEditedRef.current) return currentEmail

          const rememberedEmail = data.email || ""
          if (rememberedEmail) {
            autoFilledEmailRef.current = rememberedEmail
            return rememberedEmail
          }

          if (autoFilledEmailRef.current && currentEmail === autoFilledEmailRef.current) {
            autoFilledEmailRef.current = ""
            return ""
          }

          return currentEmail
        })
      } catch (_rememberedEmailError) {
        // Remembered email lookup is a convenience; manual entry remains available.
      }
    }, 250)

    return () => window.clearTimeout(timer)
  }, [gateId, pageData, plate, sessionIdFromUrl])

  useEffect(() => {
    if (!pageData?.site?.name) return undefined

    const previousTitle = document.title
    document.title = pageData.site.name

    return () => {
      document.title = previousTitle
    }
  }, [pageData?.site?.name])

  useEffect(() => {
    if (step !== "active") {
      setExitConfirmArmed(false)
    }
  }, [step])

  useEffect(() => {
    if (step === "vehicle") {
      warmGateCommand("entry-details")
    }

    if (step === "exit_plate" || step === "exit_payment") {
      warmGateCommand("exit-details")
    }

    if (step === "authorised") {
      warmGateCommand("entry-authorised")
    }

    if (step === "summary" && exitGates.length > 0) {
      warmGateCommand("exit-summary")
    }
  }, [step, exitGates.length])

  useEffect(() => {
    if (exitConfirmArmed) {
      warmGateCommand("exit-confirmed")
    }
  }, [exitConfirmArmed])

  const stripePromise = useMemo(() => {
    const publishableKey = pageData?.stripe?.publishable_key
    if (!pageData?.stripe?.configured || !publishableKey) return null
    return loadStripe(publishableKey)
  }, [pageData?.stripe?.configured, pageData?.stripe?.publishable_key])

  function handlePlateChange(value) {
    setPlate(value.toUpperCase())
  }

  function handleEmailChange(value) {
    emailManuallyEditedRef.current = true
    setEmail(value)
  }

  async function handleStartSession() {
    setError("")

    if (!plate.trim()) {
      setError("License plate is required")
      return
    }

    if (!email.trim()) {
      setError("Email is required")
      return
    }

    if (!isValidEmail(email.trim())) {
      setError("Enter a valid email address")
      return
    }

    setBusy(true)

    try {
      const occupancyData = await apiRequest(`/api/v1/parking/${gateId}/occupancy`)
      setOccupancy(occupancyData)
      if (Number(occupancyData.available_spots || 0) <= 0) {
        setStep("full")
        return
      }

      const requiresPayment = Boolean(pageData?.stripe?.configured)
      const data = await apiRequest(`/api/v1/parking/${gateId}/start`, {
        method: "POST",
        body: JSON.stringify({
          plate,
          email,
          remember_email: rememberEmail
        })
      })

      setParkingSession(data.parking_session)
      if (data.resumed) {
        if (data.metrics) setMetrics(data.metrics)
        setStep(stepForResumedSession(data.parking_session.status, requiresPayment))
        return
      }

      if (requiresPayment) {
        setStep("payment")
      } else {
        setStep("authorised")
      }
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleAuthorisePayment(paymentMethodId) {
    setError("")

    if (!paymentMethodId) {
      setError("Card details are required")
      return
    }

    if (!parkingSession) {
      setError("Parking session not found")
      return
    }

    setBusy(true)
    setStep("authorising")

    try {
      const data = await apiRequest(`/api/v1/parking/${gateId}/authorise`, {
        method: "POST",
        body: JSON.stringify({
          session_id: parkingSession.id,
          payment_method_id: paymentMethodId,
          receipt_email: email
        })
      })

      setParkingSession(data.parking_session)
      setStep("authorised")
    } catch (requestError) {
      setError(requestError.message)
      setStep("payment")
    } finally {
      setBusy(false)
    }
  }

  async function handleOpenGate() {
    if (!parkingSession) {
      setError("Parking session not found")
      return
    }

    setError("")
    setBusy(true)

    try {
      const occupancyData = await apiRequest(`/api/v1/parking/${gateId}/occupancy`)
      setOccupancy(occupancyData)
      if (Number(occupancyData.available_spots || 0) <= 0) {
        setStep("full")
        return
      }

      const data = await apiRequest(`/api/v1/parking/${gateId}/open_gate`, {
        method: "POST",
        body: JSON.stringify({ session_id: parkingSession.id })
      })

      setParkingSession(data.parking_session)
      setOccupancy(extractOccupancy(data))
      setStep("active")
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleExit() {
    if (!parkingSession) {
      setError("Parking session not found")
      return
    }

    setError("")
    setBusy(true)
    setOpenedExitGate(null)
    setStep("processing_exit")

    try {
      const data = await apiRequest(`/api/v1/parking/${gateId}/exit`, {
        method: "POST",
        body: JSON.stringify({
          session_id: parkingSession.id,
          receipt_email: email
        })
      })

      setParkingSession(data.parking_session)
      setSummary(data.summary)
      setExitGates(data.exit_gates || [])
      setOccupancy(extractOccupancy(data))
      setStep("summary")
    } catch (requestError) {
      setError(requestError.message)
      setStep("active")
    } finally {
      setBusy(false)
    }
  }

  async function handleExitLookup() {
    setError("")

    if (!plate.trim()) {
      setError("License plate is required")
      return
    }

    setBusy(true)
    setOpenedExitGate(null)

    try {
      const data = await apiRequest(`/api/v1/parking/${gateId}/exit_lookup`, {
        method: "POST",
        body: JSON.stringify({ plate })
      })

      setParkingSession(data.parking_session)
      setMetrics(data.metrics || null)
      setSummary(data.summary || null)
      setExitGates(data.exit_gates || [])
      setOccupancy(extractOccupancy(data))

      if (data.summary?.payment_required) {
        setStep("exit_payment")
      } else {
        await completeExit({ sessionId: data.parking_session.id })
      }
    } catch (requestError) {
      setError(requestError.message)
      setStep("exit_plate")
    } finally {
      setBusy(false)
    }
  }

  async function completeExit({ sessionId, paymentMethodId = null }) {
    setError("")
    setBusy(true)
    setStep("processing_exit")

    try {
      const data = await apiRequest(`/api/v1/parking/${gateId}/pay_exit`, {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          payment_method_id: paymentMethodId,
          receipt_email: email
        })
      })

      setParkingSession(data.parking_session)
      setSummary(data.summary)
      setExitGates(data.exit_gates || [])
      setOccupancy(extractOccupancy(data))
      setStep("summary")
    } catch (requestError) {
      setError(requestError.message)
      setStep(paymentMethodId ? "exit_payment" : "exit_plate")
    } finally {
      setBusy(false)
    }
  }

  async function handlePayExitPayment(paymentMethodId) {
    if (!parkingSession) {
      setError("Parking session not found")
      return
    }

    if (!paymentMethodId) {
      setError("Card details are required")
      return
    }

    completeExit({ sessionId: parkingSession.id, paymentMethodId })
  }

  function handleExitClick() {
    if (!exitConfirmArmed) {
      setExitConfirmArmed(true)
      return
    }

    handleExit()
  }

  async function handleOpenExitGate(exitGateId) {
    if (!parkingSession) {
      setError("Parking session not found")
      return
    }

    if (!exitGateId) {
      setError("Select an exit gate")
      return
    }

    setError("")
    setBusy(true)

    try {
      const data = await apiRequest(`/api/v1/parking/${gateId}/open_exit_gate`, {
        method: "POST",
        body: JSON.stringify({
          session_id: parkingSession.id,
          exit_gate_id: exitGateId
        })
      })

      setOpenedExitGate(data.gate || null)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusy(false)
    }
  }

  function warmGateCommand(key) {
    if (!pageData?.gate_control?.warmup_available || gateWarmupKeys[key]) return

    setGateWarmupKeys((previous) => ({ ...previous, [key]: true }))
    fetch(`/api/v1/parking/${gateId}/warm_gate_command`, {
      method: "POST",
      keepalive: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    }).catch(() => {})
  }

  if (loading) {
    return <div className="parking-loading">Loading parking details...</div>
  }

  if (error && !pageData) {
    return (
      <div className="parking-shell">
        <div className="parking-steps-track">
          <section className="parking-card">
            <div className="parking-card-header parking-card-header-light"><h2>Parking</h2></div>
            <div className="parking-card-body">
              <div className="parking-inline-error parking-inline-error-in-card">{error}</div>
            </div>
          </section>
        </div>
      </div>
    )
  }

  const site = pageData.site
  const tenant = pageData.tenant || {}
  const feeNotice = formatFeeNotice(site.fee_fixed_amount, site.fee_percentage)
  const liveAssistanceLink = gateId && pageData?.site?.ai_attendant_enabled ? `/help/${gateId}` : ""
  const freeMinutesActive = Number(site.free_minutes_active || 0)
  const availableSpots = Number(occupancy?.available_spots || 0)
  const isCarParkFull = occupancy && availableSpots <= 0
  const showCardError = Boolean(error)
  const renderCardError = () => (
    showCardError ? <div className="parking-inline-error parking-inline-error-in-card">{error}</div> : null
  )

  function renderCurrentStep() {
    switch (step) {
      case "landing":
        return (
          <section className="parking-card parking-card-landing">
            <div className="parking-card-header">
              <h2>{site.name}</h2>
            </div>
            <div className="parking-card-body parking-intro">
              {tenant.logo_url ? (
                <div className="parking-hero-logo-wrap">
                  <img className="parking-hero-logo" src={tenant.logo_url} alt={`${tenant.company_name || "Company"} logo`} />
                </div>
              ) : null}
              <p className="parking-hours-line">{site.hours_display || "-"}</p>
              <p className="parking-pricing-line">{formatMoney(site.hourly)}/hr · {formatMoney(site.daily_cap)} daily max</p>
              {feeNotice ? <p className="parking-fee-note">{feeNotice}</p> : null}
              {freeMinutesActive > 0 ? (
                <p className="parking-free-note">{freeMinutesActive} Minutes Free Parking</p>
              ) : null}
              {renderCardError()}
              <p className="parking-consent-copy">
                By continuing, you agree to the{" "}
                <button type="button" className="parking-link-button" onClick={() => setShowTermsModal(true)}>
                  Terms & Conditions
                </button>
                .
              </p>
              <button
                className="parking-button parking-button-primary"
                type="button"
                onClick={() => setStep("vehicle")}
              >
                Start Parking
              </button>
            </div>
          </section>
        )
      case "exit_plate":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Confirm Registration</h2></div>
            <div className="parking-card-body">
              {renderCardError()}
              <label htmlFor="plate">License Plate</label>
              <input id="plate" value={plate} onChange={(e) => handlePlateChange(e.target.value)} placeholder="Enter License Plate" />

              <button
                className="parking-button parking-button-primary"
                type="button"
                onClick={handleExitLookup}
                disabled={busy}
              >
                Continue
              </button>
            </div>
          </section>
        )
      case "exit_payment":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Pay Parking Fee</h2></div>
            <div className="parking-card-body">
              {summary ? (
                <div className="parking-summary-box">
                  <div className="parking-stat-row"><span>Total Time:</span><strong>{summary.total_time_display || "-"}</strong></div>
                  <div className="parking-stat-row"><span>Total Charge:</span><strong>{formatMoney(summary.total_charge)}</strong></div>
                </div>
              ) : null}
              <label htmlFor="exit-email">Email Receipt</label>
              <input id="exit-email" value={email} onChange={(e) => handleEmailChange(e.target.value)} placeholder="Email Address Optional" />
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <StripeCardAuthoriseForm
                    busy={busy}
                    error={error}
                    onAuthorise={handlePayExitPayment}
                    buttonText="Pay and Continue"
                    secureCopy="Your card will be charged now so you can exit."
                  />
                </Elements>
              ) : (
                <>
                  {renderCardError()}
                  <div className="parking-inline-error parking-inline-error-in-card">
                    Stripe is not configured for this site.
                  </div>
                </>
              )}
            </div>
          </section>
        )
      case "vehicle":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Enter Vehicle Details</h2></div>
            <div className="parking-card-body">
              {renderCardError()}
              {isCarParkFull ? (
                <div className="parking-inline-error parking-inline-error-in-card">
                  Car Park Full. Please try again later.
                </div>
              ) : null}
              <label htmlFor="plate">License Plate</label>
              <input id="plate" value={plate} onChange={(e) => handlePlateChange(e.target.value)} placeholder="Enter License Plate" />

              <label htmlFor="email">Email</label>
              <input id="email" value={email} onChange={(e) => handleEmailChange(e.target.value)} placeholder="Enter Email Address" />

              <label className="parking-checkbox-row" htmlFor="remember-email">
                <input
                  id="remember-email"
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                />
                Remember me
              </label>

              <button
                className="parking-button parking-button-primary"
                type="button"
                onClick={handleStartSession}
                disabled={busy || isCarParkFull}
              >
                Continue
              </button>
            </div>
          </section>
        )
      case "full":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Car Park Full</h2></div>
            <div className="parking-card-body parking-center">
              <p>Please try again later.</p>
              <button
                className="parking-button"
                type="button"
                onClick={() => setStep("vehicle")}
              >
                Back
              </button>
            </div>
          </section>
        )
      case "payment":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Enter Payment Details</h2></div>
            <div className="parking-card-body">
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <StripeCardAuthoriseForm busy={busy} error={error} onAuthorise={handleAuthorisePayment} />
                </Elements>
              ) : (
                <>
                  {renderCardError()}
                  <div className="parking-inline-error parking-inline-error-in-card">
                    Stripe is not configured for this site.
                  </div>
                </>
              )}
            </div>
          </section>
        )
      case "authorising":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Authorising Your Card</h2></div>
            <div className="parking-card-body parking-center">
              {renderCardError()}
              <div className="parking-spinner" />
              <p>Please wait...</p>
            </div>
          </section>
        )
      case "authorised":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>{pageData?.stripe?.configured ? "Authorising Your Card" : "Ready To Enter"}</h2></div>
            <div className="parking-card-body parking-center">
              {renderCardError()}
              <div className="parking-checkmark">✓</div>
              <h3>{pageData?.stripe?.configured ? "Authorisation Successful" : "Entry Ready"}</h3>
              <p>{pageData?.stripe?.configured ? "You will only be charged when you exit." : "Tap below to open the gate and enter the car park."}</p>
              <button className="parking-button parking-button-primary" type="button" onClick={handleOpenGate} disabled={busy}>
                Open and Enter Carpark
              </button>
            </div>
          </section>
        )
      case "active":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Active Parking</h2></div>
            <div className="parking-card-body">
              {renderCardError()}
              {occupancy ? (
                <p className="parking-gate-meta"><strong>{availableSpots}</strong> spots remaining</p>
              ) : null}
              <div className="parking-stat-row"><span>Entry Time:</span><strong>{formatClock(metrics?.entry_time || parkingSession?.entry_at)}</strong></div>
              <div className="parking-stat-row"><span>Time Elapsed:</span><strong>{metrics?.elapsed_display || "00:00:00"}</strong></div>
              {Number(metrics?.free_minutes || 0) > 0 ? (
                Number(metrics?.free_remaining_seconds || 0) > 0 ? (
                  <div className="parking-stat-row parking-stat-row-free">
                    <span>Free Parking Remaining:</span>
                    <strong>{metrics?.free_remaining_display || "00:00:00"}</strong>
                  </div>
                ) : (
                  <div className="parking-free-applied-note">
                    {metrics?.free_minutes} minutes free parking applied.
                  </div>
                )
              ) : null}
              <div className="parking-stat-row"><span>Estimated Cost:</span><strong>{formatMoney(metrics?.estimated_cost)}</strong></div>

              <div className="parking-status-pill">ACTIVE</div>

              <p className="parking-warning-copy">
                Only tap Finish Parking when you are at the boom gate and ready to exit.
              </p>

              <button className="parking-button parking-button-danger" type="button" onClick={handleExitClick} disabled={busy}>
                {exitConfirmArmed ? "I am at the exit gate now" : "Finish Parking"}
              </button>
            </div>
          </section>
        )
      case "processing_exit":
        return (
          <section className="parking-card">
            <div className="parking-card-header"><h2>Processing Exit</h2></div>
            <div className="parking-card-body parking-center">
              {renderCardError()}
              <div className="parking-spinner" />
              <p>{summary?.payment_required ? "Charging your card..." : "Completing your session..."}</p>
            </div>
          </section>
        )
      case "summary":
        return (
          <section className="parking-card">
            <div className="parking-card-header parking-card-header-light"><h2>Exit Summary</h2></div>
            <div className="parking-card-body">
              {renderCardError()}
              <div className="parking-summary-box">
                <div className="parking-stat-row"><span>Total Time:</span><strong>{summary?.total_time_display || "-"}</strong></div>
                <div className="parking-stat-row"><span>Total Charge:</span><strong>{formatMoney(summary?.total_charge)}</strong></div>
              </div>

              <h3 className="parking-charging-title">
                {Number(summary?.total_charge || 0) > 0 ? "Payment Successful" : "Session Complete"}
              </h3>
              <div className="parking-checkmark">✓</div>
              <h3 className="parking-charging-title">Open Exit Gate</h3>
              {exitGates.length > 0 ? (
                <div className="parking-exit-gates">
                  {exitGates.map((exitGate) => (
                    <button
                      key={exitGate.id}
                      className="parking-button parking-button-primary"
                      type="button"
                      disabled={busy || Boolean(openedExitGate)}
                      onClick={() => handleOpenExitGate(exitGate.id)}
                    >
                      Open {exitGate.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="parking-gate-meta">No exit gates configured for this site.</p>
              )}
              {openedExitGate ? (
                <p className="parking-gate-meta">Gate opened: {openedExitGate.name} · {openedExitGate.direction}</p>
              ) : null}
            </div>
          </section>
        )
      default:
        return null
    }
  }

  return (
    <div className="parking-shell">
      {isInstallable && !isInstalled && !installDismissed ? (
        <div className="parking-install-banner" role="region" aria-label="Install app">
          <span>Add ParkEngine to your home screen</span>
          <div className="parking-install-banner-actions">
            <button type="button" className="parking-install-banner-btn" onClick={() => promptInstall()}>
              Install
            </button>
            <button
              type="button"
              className="parking-install-banner-dismiss"
              aria-label="Dismiss install prompt"
              onClick={() => setInstallDismissed(true)}
            >
              ✕
            </button>
          </div>
        </div>
      ) : null}
      <div className="parking-steps-track">
        <div className="parking-step-panel" key={step}>
          {renderCurrentStep()}
          <section className="parking-company-card">
            <h3>
              {tenant.company_name || "Company"}
              {tenant.abn ? ` · ${tenant.abn}` : ""}
            </h3>
            <p>{tenant.address || "-"}</p>
            {tenant.email ? <p>Email: {tenant.email}</p> : null}
            <p>
              {liveAssistanceLink ? (
                <a href={liveAssistanceLink} target="_blank" rel="noopener noreferrer">Live Assistance</a>
              ) : null}
            </p>
          </section>
        </div>
      </div>

      {showTermsModal ? (
        <div className="parking-modal-overlay" role="dialog" aria-modal="true" aria-label="Terms and conditions">
          <div className="parking-modal-card">
            <div className="parking-modal-header">
              <h3>Terms & Conditions</h3>
              <button type="button" className="parking-modal-close" onClick={() => setShowTermsModal(false)}>
                ×
              </button>
            </div>
            <div className="parking-modal-body">
              {tenant.terms_html ? (
                <div dangerouslySetInnerHTML={{ __html: tenant.terms_html }} />
              ) : (
                <p>No terms configured for this site.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
